import { ActionFunction } from 'react-router-dom';
import { client } from '~/api/client';
import {
  parseOptionalTrimmedFormField,
  parseVisibilityFormField,
  parseWordDocumentTemplateSectionsFromFormData
} from '~/api/data/form-data';
import { mutateData } from '~/api/data/request';
import { submitMutation } from '~/api/data/responses';
import {
  CreateWordDocumentTemplateDocument,
  CreateWordDocumentTemplateMutation,
  CreateWordDocumentTemplateMutationVariables,
  UpdateWordDocumentTemplateDocument,
  UpdateWordDocumentTemplateMutation,
  UpdateWordDocumentTemplateMutationVariables,
  UpdateWordDocumentTemplatesDocument,
  UpdateWordDocumentTemplatesMutation,
  UpdateWordDocumentTemplatesMutationVariables,
  type VisibilityConfigInput
} from '~/api/graphql';

export const createWordDocumentTemplate = (
  variables: CreateWordDocumentTemplateMutationVariables
) =>
  mutateData<CreateWordDocumentTemplateMutation, CreateWordDocumentTemplateMutationVariables>(
    client,
    {
      mutation: CreateWordDocumentTemplateDocument,
      variables
    }
  );

export const updateWordDocumentTemplate = (
  variables: UpdateWordDocumentTemplateMutationVariables
) =>
  mutateData<UpdateWordDocumentTemplateMutation, UpdateWordDocumentTemplateMutationVariables>(
    client,
    {
      mutation: UpdateWordDocumentTemplateDocument,
      variables
    }
  );

export const archiveWordDocumentTemplates = (
  variables: UpdateWordDocumentTemplatesMutationVariables
) =>
  mutateData<UpdateWordDocumentTemplatesMutation, UpdateWordDocumentTemplatesMutationVariables>(
    client,
    {
      mutation: UpdateWordDocumentTemplatesDocument,
      variables
    }
  );

export const updateWordDocumentTemplatesVisibility = async ({
  templateIds,
  visibility
}: {
  templateIds: string[];
  visibility: VisibilityConfigInput;
}) => {
  try {
    await client.mutate<
      UpdateWordDocumentTemplatesMutation,
      UpdateWordDocumentTemplatesMutationVariables
    >({
      mutation: UpdateWordDocumentTemplatesDocument,
      variables: { templateIds, input: { visibility } }
    });
    return { failedIds: [] as string[] };
  } catch {
    return { failedIds: templateIds };
  }
};

export const submitArchiveWordDocumentTemplates: ActionFunction = async ({ request }) =>
  submitMutation(
    archiveWordDocumentTemplates({
      templateIds: (await request.json()).templateIds,
      input: { archive: true }
    })
  );

export const submitUpdateWordDocumentTemplatesVisibility: ActionFunction = async ({ request }) =>
  updateWordDocumentTemplatesVisibility(await request.json());

export const submitCreateWordDocumentTemplate: ActionFunction = async ({ request }) => {
  const formData = await request.formData();
  // The dialog uses sections even for a single file. The backend rejects mixing
  // sections with top-level uploadTemplate / templateFileId inputs.
  const sections = parseWordDocumentTemplateSectionsFromFormData(formData) ?? [];
  return submitMutation(
    createWordDocumentTemplate({
      input: {
        title: formData.get('title') as string,
        description: formData.get('description') as string,
        fillGuidance: parseOptionalTrimmedFormField(formData.get('fillGuidance')),
        sections,
        visibility: parseVisibilityFormField(formData.get('visibility'))
      }
    }),
    async (data) => ({ data: data ?? null })
  );
};

export const submitUpdateWordDocumentTemplate: ActionFunction = async ({ params, request }) => {
  const formData = await request.formData();
  const fillRaw = formData.get('fillGuidance');
  const sections = parseWordDocumentTemplateSectionsFromFormData(formData) ?? [];
  return submitMutation(
    updateWordDocumentTemplate({
      id: params.id || '',
      input: {
        title: formData.get('title') as string,
        description: formData.get('description') as string,
        fillGuidance: typeof fillRaw === 'string' ? fillRaw : '',
        sections,
        visibility: parseVisibilityFormField(formData.get('visibility'))
      }
    })
  );
};
